import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { Badge, Card, ListRow } from '../../ui';
import type { BadgeTone } from '../../ui';
import { listSessions, listWeeks } from '../../shared/queries';
import type { SessionRow, WeekRow } from '../../shared/queries';
import {
  formatDate,
  formatDayOfMonth,
  weekdayNames,
  weekdayNamesShort,
} from '../../shared/formatDate';
import { supabase } from '../../shared/supabase/client';
import { useInstance } from '../instance/InstanceProvider';
import { text } from '../../shared/text';
import type { MessageId } from '../../shared/text';
import { ClientGuard } from './ClientGuard';
import { AppBar, ClientLoading, ClientNotFound, ClientPage } from './ClientPage';
import { ArrowLeft, ArrowRight, ChevronRight } from './icons';
import { useSessionProgress } from './progress';
import {
  currentWeek,
  lastWeek,
  sessionDay,
  sessionState,
  today,
  weekAfter,
  weekBefore,
  weekByNumber,
  weekDays,
} from './schedule';
import type { ISODay, SessionState } from './schedule';

/**
 * The week: what is asked of the client, what is behind them, and what is being
 * prepared.
 *
 * Today answers one question about one day. This screen answers the two a
 * client actually asks on a Sunday evening — *what does this week want from me*,
 * and *did I do Monday* — and it is what turns a list of exercises into evidence
 * that somebody made a plan.
 *
 * ## What was taken from the hi-fi
 *
 * The **week strip**: seven cells, `.week-strip` / `.day-cell` in the design's
 * `app.css`, with the abbreviated weekday over the day of the month, a dot on a
 * day that has something on it, and today filled in the design's neon. It is
 * ported as written, with two deliberate changes, both forced by rules this
 * product already has:
 *
 *   * the neon `--inari-primary` is **a fill carrying dark text**, never a text
 *     colour, so today's cell is `bg-accent` with `text-on-accent` — 10.52:1,
 *     the pairing already in the contrast table in `src/index.css`. The dot is
 *     `primary`, which measures 7.34:1 on the page and 4.62:1 on the neon;
 *   * the cell has a `primary` border when it is today, for the same reason the
 *     one neon button in the product has one: #00DD8E is 1.59:1 against the page
 *     and a fill that light has no visible edge.
 *
 * **Two things next to it in the hi-fi are deliberately absent**: the streak
 * counter and the "Day 47" programme counter. The owner parked streaks on
 * 2026-09-14 and said to ignore the day counter, and `client-tone` forbids the
 * first outright — "no marker whose meaning is that the client has an unbroken
 * record to protect". The strip is taken; what sits beside it is not.
 *
 * **The strip is hidden from assistive technology.** Every cell of it is
 * repeated in words in the list underneath — the weekday, the date, what is on
 * it and its state — so announcing it twice would make the screen longer and no
 * more informative. It is a picture of the list, and it says so.
 *
 * ## The four states, and the one word that matters
 *
 * A session is done, today's, still ahead, or **not done** — `sin hacer`. That
 * last one is the most emotionally loaded state in the product and it is
 * specified in words *and* in colour: neutral wording with no synonym that
 * assigns fault, and neither the removal colour nor the attention colour, so a
 * reviewer can hold the screen next to `design-system` and get a yes or a no.
 * The badge is `neutral` — the same grey the "still ahead" badge wears — and the
 * row carries an ordinary invitation to do it, which is the same invitation
 * every other session on the screen carries.
 *
 * **There is no summary of a week anywhere on this screen**: no total, no
 * "2 of 3", no percentage, no comparison with the week before. `client-tone`
 * draws the line between reporting a fact and issuing a verdict, and a mark out
 * of three for a week is a verdict.
 *
 * ## What the publish gate does here, and what it does not
 *
 * `listWeeks` returns the weeks the caller may see, which for a client is her
 * own published ones — the policy `week_select_client_published` is the filter,
 * and a draft week is not hidden by this file, it is not in the response at all.
 * So the screen cannot show an unpublished week's contents even by accident: it
 * does not have them.
 *
 * What it *can* honestly say is that the next week is coming, because a week
 * after the last published one is a number, not a content. That message
 * attributes the wait to the coach still writing it and never to the client
 * lacking permission — see `design.md` D4. Any other week number a client types
 * into the address bar is "not found", the same answer a foreign week gets, with
 * nothing to tell the two apart.
 */
export function WeekPage() {
  return (
    <ClientGuard>
      <Week />
    </ClientGuard>
  );
}

function Week() {
  const instance = useInstance();
  const params = useParams();
  const now = today();

  const weeks = useQuery({ queryKey: ['weeks'], queryFn: () => listWeeks(supabase) });
  const published = weeks.data ?? [];

  /*
   * The address names a week by its *number*, not by its id, and that is what
   * makes the unpublished case expressible at all: a draft week has an id the
   * client will never learn, but "the week after the last one I can see" is a
   * number this screen can compute without being told anything about it.
   */
  const requested = params['weekNumber'];
  const valid = requested === undefined || /^[1-9]\d{0,3}$/.test(requested);
  const number = requested === undefined || !valid ? null : Number(requested);

  const week =
    requested === undefined ? currentWeek(published, now) : weekByNumber(published, number ?? 0);

  const sessions = useQuery({
    queryKey: ['sessions', week?.id],
    queryFn: () => listSessions(supabase, week?.id ?? ''),
    enabled: week !== null,
  });

  if (weeks.isPending) return <ClientLoading />;
  if (!valid) return <ClientNotFound />;

  if (week === null) {
    // Nothing published at all: the same sentence Today says, because it is the
    // same situation and a client should not meet two wordings for it.
    if (number === null) {
      return <NothingPublished slug={instance.slug} coach={instance.coachDisplayName} />;
    }

    const last = lastWeek(published);
    if (last !== null && number === last.week_number + 1) {
      return (
        <NotReady
          slug={instance.slug}
          coach={instance.coachDisplayName}
          number={number}
          previous={last.week_number}
        />
      );
    }

    // A week number that is not hers, or does not exist, or is further ahead
    // than the coach has got. One answer for all three.
    return <ClientNotFound />;
  }

  return (
    <WeekView
      slug={instance.slug}
      week={week}
      weeks={published}
      sessions={sessions.data ?? []}
      pending={sessions.isPending}
      now={now}
    />
  );
}

function WeekView({
  slug,
  week,
  weeks,
  sessions,
  pending,
  now,
}: {
  slug: string;
  week: WeekRow;
  weeks: readonly WeekRow[];
  sessions: readonly SessionRow[];
  pending: boolean;
  now: ISODay;
}) {
  const days = weekDays(week, now);
  const long = weekdayNames();
  const previous = weekBefore(weeks, week);
  const following = weekAfter(weeks, week);
  const last = lastWeek(weeks);

  /*
   * Forward from the last published week leads to the message that the next one
   * is being prepared, rather than to nothing. That is the one place the client
   * is allowed to learn that a week number is coming, and `design.md` D4 is why
   * it is a product statement and not a permissions one.
   */
  const nextNumber =
    following?.week_number ??
    (last !== null && week.week_number === last.week_number ? week.week_number + 1 : null);

  const byDay = new Map<number, SessionRow[]>();
  for (const session of sessions) {
    byDay.set(session.day_of_week, [...(byDay.get(session.day_of_week) ?? []), session]);
  }

  return (
    <ClientPage>
      <AppBar backTo={`/c/${slug}/today`} backLabel={text('client.week.back')} />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {text('client.today.week', { number: week.week_number })}
      </h1>

      <p className="text-ink-muted mt-1 text-sm">
        {text('client.week.range', {
          from: formatDate(days[0] ?? ''),
          to: formatDate(days[6] ?? ''),
        })}
      </p>

      <WeekNav
        slug={slug}
        previous={previous?.week_number ?? null}
        next={nextNumber}
        className="mt-4"
      />

      <WeekStrip days={days} now={now} byDay={byDay} />

      {pending ? <p className="text-ink-muted mt-4 text-sm">{text('app.loading')}</p> : null}

      {!pending && sessions.length === 0 ? (
        <p className="text-ink-soft mt-4 text-sm">{text('client.today.noSessions')}</p>
      ) : null}

      <ol className="divide-border mt-2 divide-y">
        {days.map((day, index) => {
          const onThisDay = byDay.get(index + 1) ?? [];
          const dayLine = text('client.today.dateLine', {
            weekday: long[index] ?? '',
            date: formatDate(day),
          });

          return (
            <li
              key={day}
              className={day === now ? 'bg-primary-tint rounded-[--radius-card] px-3' : ''}
            >
              {onThisDay.length === 0 ? (
                /*
                 * A day the coach put nothing on is a part of the plan, named as
                 * one. It is not a link, it carries no control and it asks the
                 * client for nothing — the failure this state is designed
                 * against is reading as data that did not load.
                 */
                <ListRow title={text('client.week.rest')} meta={dayLine} />
              ) : (
                onThisDay.map((session) => (
                  <WeekSession
                    key={session.id}
                    slug={slug}
                    session={session}
                    day={sessionDay(week, session, now)}
                    now={now}
                    weekNumber={week.week_number}
                    dayLine={dayLine}
                  />
                ))
              )}
            </li>
          );
        })}
      </ol>

      {/*
        What this screen cannot do, said on it.

        Every "Hecha" above is a note one browser wrote to itself: there is no
        `session_completion` table, so a session ticked on a phone reads "sin
        hacer" on the same client's laptop and the coach never learns that any of
        it happened. `client-coach-presence` established that the product says
        this where it matters rather than leaving it to be found, and this is
        where it matters most — a week view is what a client opens *to check what
        they have done*, and a history that empties when they change phone is a
        worse lie than a tick that does nothing.

        One sentence, smallest weight, no alarm colour, nothing asked.
      */}
      <p className="text-ink-muted mt-6 text-xs leading-relaxed">
        {text('client.week.onThisDevice')}
      </p>
    </ClientPage>
  );
}

/**
 * One session on one day, and what state it is in.
 *
 * The state is read here rather than by the week, because completion lives on
 * the device — `./progress.ts` — and `useSessionProgress` is one subscription per
 * session. A parent cannot call it in a loop, so each row owns its own.
 *
 * The link carries the week it was opened from, so that coming back from the
 * session lands on the week the client was looking at rather than on Today. It
 * is a query parameter rather than router state deliberately: it survives a
 * reload, a shared address and a notification, which router state does not.
 */
function WeekSession({
  slug,
  session,
  day,
  now,
  weekNumber,
  dayLine,
}: {
  slug: string;
  session: SessionRow;
  day: ISODay;
  now: ISODay;
  weekNumber: number;
  dayLine: string;
}) {
  const progress = useSessionProgress(session.id);
  const state = sessionState(day, now, progress.completedAt);

  return (
    <Link
      to={`/c/${slug}/sessions/${session.id}?week=${String(weekNumber)}`}
      className="block rounded-[--radius-control]"
    >
      <ListRow title={session.title} meta={dayLine} trailing={<State state={state} />} />
    </Link>
  );
}

const STATE_LABEL: Record<SessionState, MessageId> = {
  done: 'client.week.done',
  today: 'client.week.today',
  ahead: 'client.week.ahead',
  open: 'client.week.notDone',
};

/**
 * **Not done wears the same grey "still to come" wears**, and that is the whole
 * point of this table.
 *
 * `danger` means something is being removed and `attention` means a person needs
 * you; neither is available here, and the spec forbids both by name. Green is
 * kept for the session that is done, which is a fact and not a reward — it is
 * stated once, on the row, and nothing is added up.
 */
const STATE_TONE: Record<SessionState, BadgeTone> = {
  done: 'training',
  today: 'neutral',
  ahead: 'neutral',
  open: 'neutral',
};

function State({ state }: { state: SessionState }) {
  return (
    <span className="flex items-center gap-2">
      <Badge tone={STATE_TONE[state]}>{text(STATE_LABEL[state])}</Badge>
      {state === 'open' ? (
        /*
         * The way to complete it, offered on the week view as the requirement
         * asks — an ordinary verb beside an ordinary row, with no adverb of time
         * and nothing that frames it as making up for something. It goes exactly
         * where the chevron on every other row goes, to the same screen, and the
         * control that finishes it there is the same control.
         */
        <span className="text-primary text-xs font-medium">{text('client.week.doIt')}</span>
      ) : (
        <ChevronRight size={20} className="text-ink-muted" />
      )}
    </span>
  );
}

/**
 * The hi-fi's `.week-strip`, at a glance and in one line.
 *
 * Hidden from assistive technology: the list below repeats every cell of it in
 * words. See the note in this file's header.
 */
function WeekStrip({
  days,
  now,
  byDay,
}: {
  days: readonly ISODay[];
  now: ISODay;
  byDay: Map<number, SessionRow[]>;
}) {
  const short = weekdayNamesShort();

  return (
    <div className="mt-4 grid grid-cols-7 gap-1.5" aria-hidden="true">
      {days.map((day, index) => {
        const isToday = day === now;
        return (
          <div
            key={day}
            className={[
              'relative rounded-[--radius-control] border py-2 pb-4 text-center',
              isToday ? 'bg-accent border-primary' : 'border-transparent',
            ].join(' ')}
          >
            <span
              className={[
                'block text-[10px] font-medium tracking-wide',
                isToday ? 'text-on-accent' : 'text-ink-muted',
              ].join(' ')}
            >
              {short[index] ?? ''}
            </span>
            <span
              className={[
                'font-display mt-0.5 block text-lg leading-none',
                isToday ? 'text-on-accent' : 'text-ink',
              ].join(' ')}
            >
              {formatDayOfMonth(day)}
            </span>
            {(byDay.get(index + 1) ?? []).length === 0 ? null : (
              <span className="bg-primary absolute bottom-1.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full" />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Back a week, forward a week. Either may be absent; neither is ever a dead end. */
function WeekNav({
  slug,
  previous,
  next,
  className,
}: {
  slug: string;
  previous: number | null;
  next: number | null;
  className?: string;
}) {
  const link =
    'text-primary inline-flex min-h-11 items-center gap-1.5 text-sm font-medium underline-offset-2 hover:underline';

  return (
    <nav className={['flex items-center justify-between gap-3', className ?? ''].join(' ')}>
      {previous === null ? (
        <span />
      ) : (
        <Link to={`/c/${slug}/week/${String(previous)}`} className={link}>
          <ArrowLeft size={18} />
          {text('client.week.previous')}
        </Link>
      )}
      {next === null ? (
        <span />
      ) : (
        <Link to={`/c/${slug}/week/${String(next)}`} className={link}>
          {text('client.week.next')}
          <ArrowRight size={18} />
        </Link>
      )}
    </nav>
  );
}

/**
 * The week after the last published one.
 *
 * Nothing of it is shown because nothing of it is known: a draft week is not in
 * the client's query results. What is said is that her coach is writing it — not
 * that the client is blocked, unauthorised, or lacking access, which is a
 * different sentence entirely for somebody in a coaching relationship.
 */
function NotReady({
  slug,
  coach,
  number,
  previous,
}: {
  slug: string;
  coach: string;
  number: number;
  previous: number;
}) {
  return (
    <ClientPage>
      <AppBar backTo={`/c/${slug}/today`} backLabel={text('client.week.back')} />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {text('client.today.week', { number })}
      </h1>

      <Card className="mt-4">
        <h2 className="text-ink font-display text-xl">{text('client.week.notReadyTitle')}</h2>
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">
          {text('client.week.notReadyBody', { coach })}
        </p>
      </Card>

      <WeekNav slug={slug} previous={previous} next={null} className="mt-4" />
    </ClientPage>
  );
}

/** No week has been published at all. Today's sentence, on Today's screen's terms. */
function NothingPublished({ slug, coach }: { slug: string; coach: string }) {
  return (
    <ClientPage>
      <AppBar backTo={`/c/${slug}/today`} backLabel={text('client.week.back')} />

      <Card className="mt-4">
        <h1 className="text-ink font-display text-xl">{text('client.today.nothingTitle')}</h1>
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">
          {text('client.today.nothingBody', { coach })}
        </p>
      </Card>
    </ClientPage>
  );
}
